import React, {useState} from 'react'
import {css} from '@emotion/core'
import styled from '@emotion/styled'
import {MdExpandMore as LinkIcon} from 'react-icons/md'
import OutsideClickHandler from 'react-outside-click-handler'
import {isAuth0PhoneNumberFallbackPicture} from '@daedalus/core/src/auth/business/authentication'
import {getUserName} from '@daedalus/core/src/user/business'

import ProfileLinks from '../ProfileLinks'
import {useDeviceLayout} from '../../../../context/deviceLayout'
import SignInButton from '../SignInButton'
import Avatar, {AvatarType} from '../../../designSystem/Avatar'
import {mq} from '../../../../utils/breakpoints'
import {
  ShowPasswordlessLoginPayload,
  User
} from '@daedalus/core/src/auth/types/Auth'
import {
  PlusIcon,
  PlusIconWrapper
} from '../../privateDeals/PrivateDealTag/PlusIcon'

type PropsType = {
  /** Whether the user is authenticated */
  isAuthenticated: boolean

  /** Callback fired when the user tries to sign in */
  onSignIn: ({
    sourceComponent,
    flow
  }: {
    sourceComponent: ShowPasswordlessLoginPayload['sourceComponent']
    flow: ShowPasswordlessLoginPayload['flow']
  }) => void

  /** User object with metadata such as profile picture, name etc. */
  user: User | null | undefined

  /** Whether the user is Plus user */
  isMemberPlus: boolean
}

const UserWidgetWrapper = styled('div')`
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
`

const DisplayName = styled('div')(
  ({theme}) => css`
    color: ${theme.colors.defaultTextColor};
    margin-left: ${theme.layout.spacing.s300}px;
    font-size: ${theme.fonts.size.md};
    line-height: ${theme.fonts.lineHeight.md};
  `
)

const ArrowWrapper = styled('div')(
  ({theme}) => css`
    text-align: center;
    color: ${theme.colors.textInputIconDefault};
    width: ${theme.layout.spacing.s600}px;
    height: ${theme.layout.spacing.s600}px;
  `
)

const AvatarWrapper = styled('div')<{isMemberPlus: boolean}>(
  ({theme, isMemberPlus}) => css`
    padding: ${isMemberPlus ? `${theme.layout.spacing.s200}px` : '0'};
    box-shadow: ${isMemberPlus
      ? `inset 0 0 0 1px ${theme.colors.violet.v300}`
      : 'none'};
    border-radius: 50%;
    position: relative;
  `
)

const withBorderArrow = ({theme}) => css`
  &::before,
  &::after {
    display: block;
    content: '';
    position: absolute;
    transform: rotate(45deg);
    width: ${theme.layout.spacing.s300}px;
    height: ${theme.layout.spacing.s300}px;
    top: -${theme.layout.spacing.s200}px;
    right: ${theme.layout.spacing.s600}px;

    ${mq.desktopXs(`
        right: ${theme.layout.spacing.s500}px;
      `)}
  }

  &::before {
    box-shadow: ${theme.shadows.fixed};
    z-index: -1;
  }

  &::after {
    background: ${theme.colors.preferencePopupBg};
    z-index: 1;
  }
`

const popupWrapperStyles = ({theme}) => css`
  position: absolute;
  margin: -1px 0 0 0;
  min-width: 176px;
  text-align: left;
  top: 110%;
  background: ${theme.colors.preferencePopupBg};
  font-family: ${theme.fonts.family.default};
  right: -${theme.layout.spacing.s300}px;
  box-shadow: ${theme.shadows.fixed};
  border-radius: ${theme.layout.radius.md}px;
`

export const ProfileLinksPopupWrapper = styled('div')(
  popupWrapperStyles,
  withBorderArrow
)

const Username = ({user}: {user: User}) => {
  const displayName = user.name || user.phoneNumber
  return (
    displayName && (
      <DisplayName data-id="UserWidgetName">{displayName}</DisplayName>
    )
  )
}

const ICON_SIZE = 24
const AVATAR_SIZE = 32

export const UserWidget = ({
  isAuthenticated,
  onSignIn,
  user,
  isMemberPlus
}: PropsType) => {
  const {isDesktop} = useDeviceLayout()
  const [isProfileLinksOpen, setProfileLinksStatus] = useState(false)

  return isAuthenticated ? (
    <UserWidgetWrapper
      data-id="UserWidget"
      className="fs-exclude"
      onClick={() => setProfileLinksStatus(!isProfileLinksOpen)}
    >
      <AvatarWrapper isMemberPlus={isMemberPlus} data-id="avatar">
        <Avatar
          picture={user?.picture}
          name={getUserName(user)}
          type={
            isAuth0PhoneNumberFallbackPicture(user?.picture)
              ? AvatarType.Icon
              : AvatarType.Picture
          }
          size={AVATAR_SIZE}
        />
        {isMemberPlus && (
          <PlusIconWrapper>
            <PlusIcon variant="text" size="xs" translateY="0" />
          </PlusIconWrapper>
        )}
      </AvatarWrapper>
      {isDesktop && (
        <>
          <Username user={user} />
          <ArrowWrapper>
            <LinkIcon size={ICON_SIZE} />
          </ArrowWrapper>
        </>
      )}
      {isProfileLinksOpen && (
        <OutsideClickHandler
          onOutsideClick={() => setProfileLinksStatus(false)}
        >
          <ProfileLinksPopupWrapper>
            <ProfileLinks />
          </ProfileLinksPopupWrapper>
        </OutsideClickHandler>
      )}
    </UserWidgetWrapper>
  ) : (
    <SignInButton onSignIn={onSignIn} />
  )
}

export default UserWidget
